import React from 'react';
import { Box, Paper, Typography, Table, TableHead, TableRow, TableCell, TableBody, TableContainer } from '@mui/material';
import AppStyles from '../styles/AppStyles';
import ErrorState from '../components/ErrorState';
import { CardSkeleton } from '../components/SkeletonLoader';
import { useQuery } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';

export default function VetPets() {
  const { t } = useTranslation();
  const API_BASE = process.env.REACT_APP_API_BASE || 'http://localhost:3000';

  const patientsQuery = useQuery({
    queryKey: ['vetPatients'],
    queryFn: async () => {
      const token = localStorage.getItem('vetToken');
      const resp = await fetch(`${API_BASE}/api/sharing/me/patients`, { headers: { Authorization: `Bearer ${token}` } });
      const data = await resp.json();
      if (!resp.ok) throw new Error(data?.message || t('vetProfile.failedToLoadPatients'));
      return Array.isArray(data?.data) ? data.data : [];
    }
  });

  const loading = patientsQuery.isLoading;
  const error = patientsQuery.isError;
  const patients = patientsQuery.data || [];

  return (
    <Box sx={{ p: { xs: 2, md: 3 } }}>
      {loading && <CardSkeleton count={1} />}
      {error && <ErrorState />}

      {!loading && !error && (
        <Paper
          sx={{
            p: { xs: 2, md: 3 },
            borderRadius: 3,
            background: 'transparent',
            border: `1px solid ${AppStyles.colors.border.light}`,
            position: 'relative',
            overflow: 'hidden',
            '&::before': {
              content: '""',
              position: 'absolute',
              top: 0,
              left: 0,
              right: 0,
              height: '4px',
              background: `linear-gradient(90deg, ${AppStyles.colors.secondary.main} 0%, ${AppStyles.colors.primary.main} 100%)`,
            }
          }}
        >
          <Typography variant="h6" sx={{ color: AppStyles.colors.primary.dark, fontWeight: AppStyles.typography.fontWeight.semibold, mb: 2 }}>
            {t('vetProfile.accessToPets')}
          </Typography>
          <TableContainer sx={{ width: '100%' }}>
            <Table sx={{ width: '100%' }}>
              <TableHead>
                <TableRow sx={{ '& th': { color: AppStyles.colors.primary.dark, fontWeight: AppStyles.typography.fontWeight.medium } }}>
                  <TableCell>{t('vetProfile.pet')}</TableCell>
                  <TableCell>{t('vetProfile.ownerName')}</TableCell>
                  <TableCell>{t('vetProfile.ownerEmail')}</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {patients.map((p, idx) => {
                  const ownerName = p.ownerName || p.owner?.name || p.userName || p.user?.name || '-';
                  const ownerEmail = p.ownerEmail || p.owner?.email || p.userEmail || p.user?.email || '-';
                  return (
                    <TableRow 
                      key={p.petId || idx}
                      sx={{
                        '& td': {
                          borderBottom: `1px solid ${AppStyles.colors.border.light}`,
                        },
                      }}
                    >
                      <TableCell>{p.petName || '-'}</TableCell>
                      <TableCell>{ownerName}</TableCell>
                      <TableCell>{ownerEmail}</TableCell>
                    </TableRow>
                  );
                })}
                {patients.length === 0 && (
                  <TableRow>
                    <TableCell colSpan={3} sx={{ color: AppStyles.colors.text.secondary, textAlign: 'center', py: 3 }}>
                      {t('vetProfile.noPetsFound')}
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </TableContainer>
        </Paper>
      )}
    </Box>
  );
}
